import { useState } from 'react';
import { App, Button, Checkbox, Flex, Image, Spin, Typography, Upload } from 'antd';
import type { AttachmentDto } from '@/entities/attachment';
import { useDeleteAttachmentMutation, useLazyDownloadAttachmentQuery, useListAttachmentsQuery, useUpdateAttachmentMutation, useUploadAttachmentMutation } from '@/entities/attachment';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useHotkeyActions, useHotkeyScope } from '@/shared/lib/hotkeys';
import { useBreakpoint } from '@/shared/lib/hooks';
import { HotkeyTip } from '@/shared/ui/hotkey';
import './AttachmentsBlock.mobile.css';

function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export interface AttachmentsBlockProps {
  /** §13 «в несохранённом документе блок отключён» — undefined до первого сохранения. */
  documentId?: string;
  readOnly?: boolean;
  /** Не рисовать заголовок «Вложения» — блок уже стоит в секции с таким названием (телефон). */
  hideTitle?: boolean;
}

/** §13 [ПРЕДЛ], §9 [ПРЕДЛ]: вложения к документу — загрузка, «Видно клиенту», скачивание, удаление. */
export function AttachmentsBlock({ documentId, readOnly, hideTitle }: AttachmentsBlockProps) {
  const { message } = App.useApp();
  // Телефон: каждое вложение — своя строка-карточка, кнопки не меньше пальца; подсказок клавиш нет.
  const { isMobile } = useBreakpoint();
  const { data: attachments = [], isLoading: isLoadingList } = useListAttachmentsQuery(documentId ?? '', { skip: !documentId });
  const [upload, { isLoading: isUploading }] = useUploadAttachmentMutation();
  const [update] = useUpdateAttachmentMutation();
  const [remove] = useDeleteAttachmentMutation();
  const [download] = useLazyDownloadAttachmentQuery();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  // Элемент блока загрузки — чтобы `Alt+A` мог открыть выбор файла, как клик по кнопке.
  const [uploadRoot, setUploadRoot] = useState<HTMLDivElement | null>(null);

  const addFile = useHotkeyActions(
    documentId && !readOnly
      ? {
          'attachments.add': {
            hint: false,
            disabled: isUploading ? 'Файл загружается' : attachments.length >= 10 ? 'Не больше 10 вложений' : false,
            run: () => uploadRoot?.querySelector<HTMLInputElement>('input[type="file"]')?.click(),
          },
        }
      : {},
  );
  useHotkeyScope(addFile.bindings);

  if (!documentId) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Вложения будут доступны после сохранения документа
      </Typography.Text>
    );
  }

  async function handleUpload(file: File): Promise<boolean> {
    try {
      await upload({ documentId: documentId!, file }).unwrap();
    } catch (e) {
      message.error(translateApiError(e));
    }
    return false; // не даём antd Upload самому слать запрос — уже отправили через RTK Query
  }

  async function handleOpen(attachment: AttachmentDto) {
    try {
      const blob = await download(attachment.id).unwrap();
      const url = URL.createObjectURL(blob);
      if (isImageMime(attachment.mime)) {
        setPreviewUrl(url);
      } else {
        const link = window.document.createElement('a');
        link.href = url;
        link.download = attachment.fileName;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleDelete(id: string) {
    try {
      await remove({ id, documentId: documentId! }).unwrap();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <Flex vertical gap={4}>
      {hideTitle ? null : (
        <Typography.Text style={{ fontSize: 12 }} strong>
          Вложения
        </Typography.Text>
      )}
      {isLoadingList ? (
        <Flex gap={8} align="center">
          <Spin size="small" />
          <Typography.Text type="secondary" style={{ fontSize: isMobile ? 13 : 12 }}>
            Загрузка вложений…
          </Typography.Text>
        </Flex>
      ) : null}
      {attachments.map((a) =>
        isMobile ? (
          <div key={a.id} style={{ border: '1px solid #d9d9d9', padding: '8px 12px' }}>
            <a onClick={() => void handleOpen(a)} style={{ display: 'block', minHeight: 32, overflowWrap: 'anywhere' }}>
              {a.fileName}
            </a>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatSize(a.sizeBytes)}
            </Typography.Text>
            <Checkbox
              checked={a.visibleToClient}
              disabled={readOnly}
              style={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
              onChange={(e) => void update({ id: a.id, documentId, visibleToClient: e.target.checked }).unwrap().catch((err) => message.error(translateApiError(err)))}
            >
              Видно клиенту
            </Checkbox>
            {!readOnly ? (
              <Button danger block size="large" style={{ minHeight: 44, marginTop: 4 }} onClick={() => void handleDelete(a.id)}>
                Удалить
              </Button>
            ) : null}
          </div>
        ) : (
          <Flex key={a.id} gap={8} align="center" wrap>
            <a onClick={() => void handleOpen(a)}>{a.fileName}</a>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {formatSize(a.sizeBytes)}
            </Typography.Text>
            <Checkbox
              checked={a.visibleToClient}
              disabled={readOnly}
              onChange={(e) => void update({ id: a.id, documentId, visibleToClient: e.target.checked }).unwrap().catch((err) => message.error(translateApiError(err)))}
            >
              Видно клиенту
            </Checkbox>
            {!readOnly ? (
              <Button size="small" danger onClick={() => void handleDelete(a.id)}>
                Удалить
              </Button>
            ) : null}
          </Flex>
        ),
      )}
      {!readOnly ? (
        <div ref={setUploadRoot} className={isMobile ? 'attachments-upload' : undefined}>
          {isMobile ? (
            // Файлы, фото из галереи и снимок с камеры — выбор предлагает сам iOS (ограничение `accept` не
            // ставим: сервер принимает любые типы).
            <Upload beforeUpload={(file) => handleUpload(file)} showUploadList={false} disabled={isUploading || attachments.length >= 10}>
              <Button block size="large" loading={isUploading} style={{ minHeight: 48 }}>
                Добавить файл или фото
              </Button>
            </Upload>
          ) : (
            <Upload beforeUpload={(file) => handleUpload(file)} showUploadList={false} disabled={isUploading || attachments.length >= 10}>
              <HotkeyTip action={addFile.get('attachments.add')} label="Добавить файл" display="inline-flex">
                <Button size="small" loading={isUploading}>
                  Добавить файл
                </Button>
              </HotkeyTip>
            </Upload>
          )}
        </div>
      ) : null}
      {previewUrl ? (
        <Image
          width={0}
          style={{ display: 'none' }}
          src={previewUrl}
          preview={{
            visible: true,
            src: previewUrl,
            onVisibleChange: (visible) => {
              if (!visible) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(undefined);
              }
            },
          }}
        />
      ) : null}
    </Flex>
  );
}
